import { Params } from '@angular/router';
import { Injectable } from '@angular/core';
import { ActivityService } from '..';
import { BehaviorSubject, Observable, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  readonly lastRoutedUrlKey: string = 'lastRoutedUrl';
  private params = new BehaviorSubject<Params>({});
  constructor(private activityService: ActivityService) {
    this.setParamsFromStorage();

    this.activityService.advanceActivityEvent.pipe(take(1)).subscribe(() => {
      const activityKeys = [
        'activityId',
        'activityOid',
        'resume',
        'isAssignment',
      ];
      this.clearStorageParams(activityKeys); //when we've launched an activity we no longer need the info for the activity launch.
    });
  }

  /**
   * Stores the given value as the last routed url in local storage.
   * @param url the url to store in local storage
   */
  setLastRoutedUrl(url: string | undefined): void {
    localStorage.removeItem(this.lastRoutedUrlKey);
    if (url) {
      //Store the url and the expiration time (10 seconds)
      const expireSeconds = 10;
      const urlInfo = { url, expires: Date.now() + 1000 * expireSeconds };
      localStorage.setItem(this.lastRoutedUrlKey, JSON.stringify(urlInfo));
    }
  }

  /**
   * Gets the last routed url from local storage, if it exists and has not expired.
   * @returns the last routed url from local storage
   */
  getLastRoutedUrl(): string | null {
    const urlInfo = localStorage.getItem(this.lastRoutedUrlKey);
    if (!urlInfo) return null;
    const url = JSON.parse(urlInfo);
    return url?.expires >= Date.now() ? url.url : null;
  }

  /**
   * This function will .next the url service params state as well as setting the params into local storage
   * @param params params from a url
   */
  setParamsInStorage(params: Params): void {
    this.params.next(params);
    localStorage.setItem('urlParams', JSON.stringify(params));
  }

  getParams(): Observable<Params> {
    return this.params.asObservable();
  }

  /**
   * This function will clear the local storage of the given keys,
   * or if no keys are given, it will clear the entire urlParams object from local storage.
   *
   * @param {string[]} [keysToRemove] - The keys to remove from the local storage
   * */
  clearStorageParams(keysToRemove?: string[]): void {
    const params = localStorage.getItem('urlParams');
    if (params) {
      if (keysToRemove) {
        const parsedParams = JSON.parse(params);
        keysToRemove.forEach(key => delete parsedParams[key]);
        if (Object.keys(parsedParams).length > 0) {
          //if there is anything left in the parsed params
          localStorage.setItem('urlParams', JSON.stringify(parsedParams));
          return;
        }
      }
      localStorage.removeItem('urlParams');
    }
  }

  private setParamsFromStorage(): void {
    const params = JSON.parse(localStorage.getItem('urlParams') ?? '{}');
    if (params && params.activityOid) {
      this.params.next({ ...this.params.value, ...params });
    }
  }
}
